import { isRequestCanceled } from '@/shared/errors/is-request-canceled';

export const INCIDENT_CLASSIFICATIONS = ['incident', 'operator_warning', 'business_error', 'canceled'] as const;
export type IncidentClassification = (typeof INCIDENT_CLASSIFICATIONS)[number];
export type IncidentSink = 'launchdarkly' | 'durable_log' | 'incident_sink';

export type InMemoryIncidentSuppressionDecision = {
    suppressed: boolean;
    reason: 'missing_key' | 'first_occurrence' | 'duplicate_suppressed';
    duplicateCount: number;
};

export type InMemoryIncidentSuppressionReservation = InMemoryIncidentSuppressionDecision & {
    commit: () => void;
    rollback: () => void;
};

const BUSINESS_ERROR_CATEGORIES = new Set([
    'authentication',
    'authorization',
    'validation',
    'not_found',
    'conflict',
    'quota',
]);
const OPERATOR_WARNING_CATEGORIES = new Set(['rate_limit']);
const CLIENT_ERROR_STATUS_CODES = new Set([400, 401, 403, 404, 409, 422]);
const INCIDENT_CLASSIFICATION_SET = new Set<string>(INCIDENT_CLASSIFICATIONS);

const isObjectRecord = (value: unknown): value is Record<string, unknown> => {
    return Object.prototype.toString.call(value) === '[object Object]';
};

const readOptionalString = (value: unknown): string | null => {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim();
    return normalized ? normalized : null;
};

const readOptionalNumber = (value: unknown): number | null => {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

export const parseIncidentClassification = (value: unknown): IncidentClassification | null => {
    const normalized = readOptionalString(value);
    if (!normalized || !INCIDENT_CLASSIFICATION_SET.has(normalized)) {
        return null;
    }

    return normalized as IncidentClassification;
};

const isBusinessErrorLike = (error: unknown): boolean => {
    if (!(error instanceof Error) && !isObjectRecord(error)) {
        return false;
    }

    const candidate = (error instanceof Error ? error : error) as Error & Record<string, unknown>;
    return (
        candidate.name === 'ValidationException' ||
        candidate.name === 'MaxPublicError' ||
        Object.prototype.hasOwnProperty.call(candidate, 'validationErrors')
    );
};

export const buildIncidentFingerprint = (...parts: unknown[]): string | null => {
    const normalizedParts = parts
        .map((value) => {
            if (typeof value === 'string') {
                const normalized = value.trim().replace(/\s+/g, '_');
                return normalized ? normalized : null;
            }

            if (typeof value === 'number' || typeof value === 'boolean') {
                return String(value);
            }

            return null;
        })
        .filter((value): value is string => !!value);

    return normalizedParts.length ? normalizedParts.join(':') : null;
};

export const classifyIncident = ({
    classification,
    category,
    status,
    code,
    error,
}: {
    classification?: IncidentClassification | null;
    category?: unknown;
    status?: unknown;
    code?: unknown;
    error?: unknown;
}): IncidentClassification => {
    const explicitClassification = parseIncidentClassification(classification);
    if (explicitClassification) {
        return explicitClassification;
    }

    const normalizedCategory = readOptionalString(category)?.toLowerCase();
    const normalizedCode = readOptionalString(code)?.toUpperCase();
    const normalizedStatus = readOptionalNumber(status);

    if (
        normalizedCategory === 'canceled' ||
        normalizedCategory === 'cancelled' ||
        normalizedCategory === 'aborted' ||
        normalizedCode === 'ERR_CANCELED' ||
        isRequestCanceled(error)
    ) {
        return 'canceled';
    }

    if (normalizedCategory && BUSINESS_ERROR_CATEGORIES.has(normalizedCategory)) {
        return 'business_error';
    }

    if (normalizedCategory && OPERATOR_WARNING_CATEGORIES.has(normalizedCategory)) {
        return 'operator_warning';
    }

    if (normalizedCategory) {
        return 'incident';
    }

    if ((normalizedStatus !== null && CLIENT_ERROR_STATUS_CODES.has(normalizedStatus)) || isBusinessErrorLike(error)) {
        return 'business_error';
    }

    if (normalizedStatus === 429) {
        return 'operator_warning';
    }

    return 'incident';
};

export const shouldEmitIncidentToSink = ({
    classification,
    sink,
}: {
    classification: IncidentClassification;
    sink: IncidentSink;
}): boolean => {
    if (classification === 'incident') {
        return true;
    }

    if (classification === 'operator_warning') {
        return sink === 'launchdarkly' || sink === 'durable_log' || sink === 'incident_sink';
    }

    return false;
};

export const createInMemoryIncidentSuppressionStore = ({
    defaultWindowMs,
    maxEntries = 256,
}: {
    defaultWindowMs: number;
    maxEntries?: number;
}) => {
    const seen = new Map<
        string,
        {
            expiresAtMs: number;
            duplicateCount: number;
        }
    >();

    const prune = (nowMs: number) => {
        for (const [key, entry] of seen.entries()) {
            if (entry.expiresAtMs <= nowMs) {
                seen.delete(key);
            }
        }

        while (seen.size > maxEntries) {
            const oldestKey = seen.keys().next().value;
            if (!oldestKey) {
                break;
            }

            seen.delete(oldestKey);
        }
    };

    const createNoopReservation = ({
        suppressed,
        reason,
        duplicateCount,
    }: InMemoryIncidentSuppressionDecision): InMemoryIncidentSuppressionReservation => ({
        suppressed,
        reason,
        duplicateCount,
        commit: () => undefined,
        rollback: () => undefined,
    });

    const reserve = ({
        key,
        nowMs = Date.now(),
        windowMs = defaultWindowMs,
    }: {
        key: string | null | undefined;
        nowMs?: number;
        windowMs?: number;
    }): InMemoryIncidentSuppressionReservation => {
        const normalizedKey = readOptionalString(key);
        if (!normalizedKey) {
            return createNoopReservation({
                suppressed: false,
                reason: 'missing_key',
                duplicateCount: 0,
            });
        }

        prune(nowMs);

        const existingEntry = seen.get(normalizedKey);
        if (existingEntry && existingEntry.expiresAtMs > nowMs) {
            existingEntry.expiresAtMs = nowMs + windowMs;
            existingEntry.duplicateCount += 1;

            return createNoopReservation({
                suppressed: true,
                reason: 'duplicate_suppressed',
                duplicateCount: existingEntry.duplicateCount,
            });
        }

        const reservedEntry = {
            expiresAtMs: nowMs + windowMs,
            duplicateCount: 0,
        };
        seen.set(normalizedKey, reservedEntry);

        let finalized = false;
        const finalize = (action: 'commit' | 'rollback') => {
            if (finalized) {
                return;
            }

            finalized = true;
            if (action === 'rollback' && seen.get(normalizedKey) === reservedEntry) {
                seen.delete(normalizedKey);
            }
        };

        return {
            suppressed: false,
            reason: 'first_occurrence',
            duplicateCount: 0,
            commit: () => finalize('commit'),
            rollback: () => finalize('rollback'),
        };
    };

    return {
        reserve,
        evaluate({
            key,
            nowMs = Date.now(),
            windowMs = defaultWindowMs,
        }: {
            key: string | null | undefined;
            nowMs?: number;
            windowMs?: number;
        }): InMemoryIncidentSuppressionDecision {
            const reservation = reserve({
                key,
                nowMs,
                windowMs,
            });
            if (!reservation.suppressed) {
                reservation.commit();
            }

            return {
                suppressed: reservation.suppressed,
                reason: reservation.reason,
                duplicateCount: reservation.duplicateCount,
            };
        },
        reset() {
            seen.clear();
        },
    };
};
